import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

function emptyCanvas(canvas) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
}

function canvasHasInk(canvas) {
  if (!canvas) return false;
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] > 0) return true;
  }
  return false;
}

function fileToJpegDataUrl(file, maxSide = 960, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read image'));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => reject(new Error('Invalid image'));
      img.src = String(reader.result || '');
    };
    reader.readAsDataURL(file);
  });
}

function photoHasSrc(p) {
  if (!p) return false;
  if (typeof p === 'string') {
    return p.startsWith('data:') || /^https?:\/\//i.test(p) || p.startsWith('/api/');
  }
  const raw = String(p.dataUrl || p.data_url || p.url || p.src || '').trim();
  return !!(raw && (raw.startsWith('data:') || /^https?:\/\//i.test(raw) || raw.startsWith('/api/')));
}

function countEmbeddablePhotos(units) {
  let n = 0;
  for (const u of units || []) {
    for (const ln of u.checklistLines || []) {
      for (const p of ln.photos || []) if (photoHasSrc(p)) n += 1;
    }
    for (const p of u.photos || []) if (photoHasSrc(p)) n += 1;
  }
  return n;
}

function SignaturePad({ onReady }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';

    const pos = (ev) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      return {
        x: ((clientX - rect.left) / rect.width) * canvas.width,
        y: ((clientY - rect.top) / rect.height) * canvas.height,
      };
    };

    const start = (ev) => {
      ev.preventDefault();
      drawing.current = true;
      const p = pos(ev);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    };
    const move = (ev) => {
      if (!drawing.current) return;
      ev.preventDefault();
      const p = pos(ev);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    };
    const end = () => {
      drawing.current = false;
    };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);

    onReady?.(canvas);

    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [onReady]);

  return (
    <canvas
      ref={canvasRef}
      width={480}
      height={120}
      className="w-full border border-gray-300 rounded bg-white touch-none"
      aria-label="IQP signature"
    />
  );
}

function mapUnitsFromRow(rowUnits) {
  return (Array.isArray(rowUnits) ? rowUnits : []).map((u) => ({
    unitId: u.unitId || u.id || u.label,
    label: u.label || u.unitId || u.id,
    outcome: String(u.outcome || 'PASS').toUpperCase() === 'FAIL' ? 'FAIL' : 'PASS',
    note: u.note || '',
    failReason: u.failReason || '',
    checklistLines: Array.isArray(u.checklistLines) ? u.checklistLines : [],
    failLines: Array.isArray(u.failLines) ? u.failLines : [],
    photos: Array.isArray(u.photos) ? u.photos : [],
  }));
}

/**
 * Merge richer checklist/photos from durable unit states when the issued
 * snapshot only has path-only (non-embeddable) photo refs.
 */
function mergeUnitsWithStates(baseUnits, states) {
  if (!Array.isArray(states) || !states.length) return baseUnits;
  return baseUnits.map((u) => {
    const st = states.find(
      (s) =>
        String(s.ss_unit_id) === String(u.unitId) ||
        String(s.ss_unit_id) === String(u.id)
    );
    if (!st) return u;
    const stateLines = Array.isArray(st.checklist_lines) ? st.checklist_lines : [];
    const baseCount = countEmbeddablePhotos([u]);
    const stateCount = countEmbeddablePhotos([{ checklistLines: stateLines, photos: [] }]);
    if (stateCount > baseCount) {
      return {
        ...u,
        checklistLines: stateLines.length ? stateLines : u.checklistLines,
        failLines: Array.isArray(st.fail_lines) && st.fail_lines.length ? st.fail_lines : u.failLines,
        outcome: st.outcome === 'FAIL' || st.outcome === 'PASS' ? st.outcome : u.outcome,
      };
    }
    return u;
  });
}

export default function PaperworkAmendModal({ buildingId, row, workers = [], onClose, onSuccess }) {
  const [units, setUnits] = useState(() => mapUnitsFromRow(row?.units));
  const [workerId, setWorkerId] = useState(row?.iqp_id || '');
  const [password, setPassword] = useState('');
  const [amendReason, setAmendReason] = useState('');
  const [imrComplete, setImrComplete] = useState(row?.imr_complete !== false);
  const [imrNotes, setImrNotes] = useState(row?.imr_notes || '');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLabel, setPreviewLabel] = useState('');
  const [evidenceBusy, setEvidenceBusy] = useState(false);
  const canvasRef = useRef(null);

  const failCount = units.filter((u) => u.outcome === 'FAIL').length;
  const passCount = units.length - failCount;
  const photoCount = countEmbeddablePhotos(units);

  const statesQuery = useQuery({
    queryKey: ['unit-states-amend', buildingId, row?.ss_code],
    queryFn: async () => {
      const res = await api.get(`/buildings/${buildingId}/unit-inspection-states`, {
        params: { ssCode: row.ss_code },
        skipErrorToast: true,
      });
      return res.data?.data || [];
    },
    enabled: !!buildingId && !!row?.ss_code,
  });

  useEffect(() => {
    if (!statesQuery.data) return;
    setUnits((prev) => mergeUnitsWithStates(prev, statesQuery.data));
  }, [statesQuery.data]);

  const bindCanvas = useCallback((canvas) => {
    canvasRef.current = canvas;
    emptyCanvas(canvas);
  }, []);

  const payload = () => ({
    ssCode: row.ss_code,
    workerId,
    units,
    imrComplete: failCount === 0 ? imrComplete : false,
    imrNotes: imrNotes.trim(),
    amendReason: amendReason.trim() || undefined,
  });

  const previewMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/buildings/${buildingId}/paperwork/amend-preview`, payload())).data?.data,
    onSuccess: (data) => {
      setPreviewHtml(data?.html || '');
      setPreviewLabel(data?.label || data?.decision || '');
      if (!data?.html) toast.error('Empty preview');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Preview failed'),
  });

  const amendMutation = useMutation({
    mutationFn: async () => {
      const canvas = canvasRef.current;
      if (!canvasHasInk(canvas)) {
        throw new Error('IQP signature required');
      }
      if (photoCount === 0) {
        const ok = window.confirm(
          'No embeddable evidence photos on this amend.\n\n' +
            'Form 12A never shows photos. The Inspection report will also have none unless you upload photos below (from the phone, copy to PC first).\n\n' +
            'Continue amend without photos?'
        );
        if (!ok) throw new Error('Cancelled — add evidence photos first');
      }
      const signature = canvas.toDataURL('image/png');
      return (
        await api.post(`/buildings/${buildingId}/paperwork/amend`, {
          ...payload(),
          password,
          signature,
        })
      ).data?.data;
    },
    onSuccess: (data) => {
      toast.success(`Amended — ${data?.label || 'issued'} (rev ${data?.revision || ''})`);
      onSuccess?.();
      onClose();
    },
    onError: (e) => {
      const msg = e.message || e.response?.data?.error || 'Amend failed';
      if (!/cancelled/i.test(msg)) toast.error(msg);
    },
  });

  const toggleOutcome = (idx) => {
    setUnits((prev) =>
      prev.map((u, i) =>
        i === idx ? { ...u, outcome: u.outcome === 'FAIL' ? 'PASS' : 'FAIL' } : u
      )
    );
    setPreviewHtml('');
  };

  const onEvidenceFiles = async (ev) => {
    const files = Array.from(ev.target.files || []).filter((f) => /^image\//i.test(f.type));
    ev.target.value = '';
    if (!files.length) return;
    if (!units.length) {
      toast.error('No units on this record');
      return;
    }
    setEvidenceBusy(true);
    try {
      const dataUrls = [];
      for (const f of files) {
        dataUrls.push(await fileToJpegDataUrl(f));
      }
      setUnits((prev) => {
        const next = prev.map((u) => ({ ...u }));
        // Attach to first unit that has checklist lines; else first unit.
        let targetIdx = next.findIndex((u) => (u.checklistLines || []).length > 0);
        if (targetIdx < 0) targetIdx = 0;
        const u = { ...next[targetIdx] };
        const lines = Array.isArray(u.checklistLines) ? u.checklistLines.map((ln) => ({ ...ln })) : [];
        if (lines.length) {
          const ln = { ...lines[0], photos: [...(lines[0].photos || [])] };
          for (const dataUrl of dataUrls) ln.photos.push({ dataUrl });
          lines[0] = ln;
          u.checklistLines = lines;
        } else {
          u.photos = [...(u.photos || []), ...dataUrls.map((dataUrl) => ({ dataUrl }))];
        }
        next[targetIdx] = u;
        return next;
      });
      setPreviewHtml('');
      toast.success(`Added ${dataUrls.length} photo(s) for Inspection report`);
    } catch (e) {
      toast.error(e.message || 'Photo attach failed');
    } finally {
      setEvidenceBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b">
          <div>
            <h3 className="font-semibold text-gray-900">Amend paperwork — {row.ss_code}</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Current: {row.paperwork_short || row.paperwork_label || row.paperwork_type}
              {row.revision ? ` · rev ${row.revision}` : ''}. IQP re-signs and enters password to
              confirm.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:text-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-sm">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-950 text-xs leading-relaxed">
            <p className="font-semibold mb-1">Simple path: upload photos here</p>
            <p>
              Select JPG/PNG below → sign → <strong>Amend &amp; re-issue</strong>. Photos go into
              Documents → Picture and the new <strong>Inspection report</strong> (not Form 12A).
              Attached now: <strong>{photoCount}</strong>.
            </p>
          </div>

          <div>
            <p className="font-medium text-gray-800 mb-2">Unit results</p>
            <div className="rounded-lg border divide-y max-h-40 overflow-y-auto">
              {units.map((u, idx) => (
                <div key={u.unitId || idx} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="truncate">{u.label || u.unitId}</span>
                  <button
                    type="button"
                    onClick={() => toggleOutcome(idx)}
                    className={`shrink-0 px-2 py-0.5 rounded text-xs font-semibold ${
                      u.outcome === 'FAIL'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {u.outcome}
                  </button>
                </div>
              ))}
              {!units.length && (
                <p className="px-3 py-4 text-gray-500">No unit snapshot on record.</p>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {passCount} pass, {failCount} fail — paperwork type recalculated on amend.
            </p>
          </div>

          <label className="block">
            <span className="font-medium text-gray-800">Upload photos (for Inspection report)</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png"
              multiple
              disabled={evidenceBusy || !units.length}
              className="mt-1 block w-full text-sm"
              onChange={onEvidenceFiles}
            />
            <span className="block text-xs text-gray-500 mt-1">
              {evidenceBusy ? 'Processing…' : 'Phone → PC → select files here. Then Amend & re-issue.'}
            </span>
          </label>

          {failCount === 0 && (
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={imrComplete}
                  onChange={(e) => setImrComplete(e.target.checked)}
                />
                IMR procedures fully complied with (12 months)
              </label>
              {!imrComplete && (
                <textarea
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  rows={2}
                  placeholder="Missed / incomplete IMR procedures"
                  value={imrNotes}
                  onChange={(e) => setImrNotes(e.target.value)}
                />
              )}
            </div>
          )}

          <label className="block">
            IQP (certificate holder)
            <select
              className="mt-1 w-full border rounded px-2 py-1.5"
              value={workerId}
              onChange={(e) => setWorkerId(e.target.value)}
            >
              <option value="">Select IQP…</option>
              {workers.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name || w.id}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            Amend reason (optional)
            <input
              type="text"
              className="mt-1 w-full border rounded px-2 py-1.5"
              value={amendReason}
              onChange={(e) => setAmendReason(e.target.value)}
              placeholder="e.g. add missing evidence photos"
            />
          </label>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-gray-800">IQP signature</span>
              <button
                type="button"
                className="text-xs text-primary-700 hover:underline"
                onClick={() => emptyCanvas(canvasRef.current)}
              >
                Clear
              </button>
            </div>
            <SignaturePad onReady={bindCanvas} />
          </div>

          <label className="block">
            Worker password (confirm)
            <input
              type="password"
              className="mt-1 w-full border rounded px-2 py-1.5"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>

          {previewHtml && (
            <div>
              <p className="font-medium text-gray-800 mb-1">
                Preview — {previewLabel} (statutory form; photos are on Inspection report only)
              </p>
              <iframe
                title="Amend preview"
                srcDoc={previewHtml}
                className="w-full h-48 border rounded bg-white"
                sandbox="allow-same-origin"
              />
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 px-4 py-3 border-t bg-slate-50">
          <button type="button" className="px-3 py-1.5 text-sm border rounded" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-sm border rounded bg-white disabled:opacity-50"
            disabled={previewMutation.isPending || !workerId || !units.length}
            onClick={() => previewMutation.mutate()}
          >
            {previewMutation.isPending ? 'Preview…' : 'Preview'}
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white disabled:opacity-50"
            disabled={amendMutation.isPending || !workerId || !password || !units.length}
            onClick={() => amendMutation.mutate()}
          >
            {amendMutation.isPending ? 'Amending…' : 'Amend & re-issue'}
          </button>
        </div>
      </div>
    </div>
  );
}
